import React, { FunctionComponent } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { makePhotoUrl } from '../../utils/makePhotoUrl';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import { filterByTypename } from '../../utils/graphql';
import { generateUrlWithShareParameter } from '../../utils/generateUrlWithShareParameter';
import { ShareQueryParameter } from '../../constants/social';
import useBasePageQuery from '../../hooks/useBasePageQuery';

const FundraisingHelmet: FunctionComponent = () => {
  const basePageData = useBasePageQuery();
  const {
    currentUrl,
    metaTagImageUrl,
    metaTagDescription,
  } = useNotNullableContext(PageMetaContext);
  const { t } = useTranslation('common', { useSuspense: false });

  if (!basePageData.data?.page) {
    return null;
  }

  const { title, hero, owner, relationships } = basePageData.data.page;
  const beneficiaries = relationships?.beneficiaries;
  const charities = filterByTypename(beneficiaries?.nodes ?? [], 'Charity');
  const name = owner?.name ?? t('common.defaultPageOwnerName');
  const charityName = charities[0]?.name ?? t('charity.defaultName');
  const description = t('fundraising.pageDescription', {
    name,
    charityName,
  });
  const twitterDescription = t('fundraising.twitterDescription', {
    name,
    charityName,
  });
  const imageUrl =
    metaTagImageUrl ||
    hero?.imageUrl ||
    makePhotoUrl('trainer-no-branding_web.jpg', 'CampaignHeroMedium');
  const metaTagValues: ShareQueryParameter = {
    ogImageUrl: metaTagImageUrl,
    ogDescription: metaTagDescription,
  };
  const canonicalUrl = generateUrlWithShareParameter(
    currentUrl,
    metaTagValues,
    false,
  );

  return (
    <Helmet
      title={t('fundraising.titleExtra', {
        name: owner?.name ?? '',
        charityName: charities[0]?.name,
      })}
      meta={[
        { property: 'og:title', content: title },
        {
          property: 'og:description',
          content: metaTagDescription || description,
        },
        {
          name: 'description',
          content: metaTagDescription || description,
        },
        {
          property: 'og:image',
          content: `${imageUrl}?template=size1200x630face`,
        },
        {
          property: 'og:image:secure_url',
          content: `${imageUrl}?template=size1200x630face`,
        },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:url', content: canonicalUrl },
        { name: 'twitter:card', content: 'summary_large_image' },
        {
          name: 'twitter:image:src',
          content: `${imageUrl}?template=size1200x630face`,
        },
        {
          name: 'twitter:description',
          content: metaTagDescription || twitterDescription,
        },
        {
          name: 'twitter:domain',
          content: 'justgiving.com',
        },
        { property: 'og:site_name', content: 'JustGiving' },
      ]}
    />
  );
};

export default FundraisingHelmet;
